
import React, { useEffect, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import { HeroVariantProps, getTextureClass, getOutlineStyle } from '../shared';
import { StoreImage } from '../../../ui/StoreImage';

export const HeroEditorial: React.FC<HeroVariantProps> = (props) => {
    const { theme, student, className, title, text, imageUrl, mobileImageUrl, onScrollToShop, customButtons, customBadge, heroContentPosition = 50, heroContentPositionY = 50, heroTextAlignment = 'center', heroButtonColor, heroButtonTextColor } = props;
    const [mounted, setMounted] = useState(false);

    useEffect(() => { setMounted(true); }, []);

    const titleColor = props.titleColor || '#FFFFFF';
    const isDesktop = window.innerWidth > 768;
    
    const dynamicShadow = props.titleShadowClass || 'drop-shadow-[0_4px_4px_rgba(0,0,0,0.5)]';
    
    // Force Serif if not set explicitly, for that editorial magazine look
    const fontStyle = theme?.heroTitleFont 
        ? { fontFamily: theme.heroTitleFont } 
        : { fontStyle: 'italic' };

    const cssTopPercent = 100 - heroContentPositionY;
    const cssLeftPercent = isDesktop ? heroContentPosition : 50;

    const textureClass = getTextureClass(props.heroTitleTexture);
    
    const titleOutline = getOutlineStyle(props.heroTitleOutlineWidth, props.heroTitleOutlineColor || '#FFFFFF');
    const textOutline = getOutlineStyle(props.heroTextOutlineWidth, props.heroTextOutlineColor || '#FFFFFF');

    const altText = title ? title.replace(/<[^>]*>?/gm, '') : "Kaffeförsäljning till klasskassan";

    return (
        <div className={`relative ${props.heightClass} overflow-hidden bg-stone-900`}>
            {/* Image */}
            <div className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${mounted ? 'opacity-80' : 'opacity-0'}`}>
                <div className={`w-full h-full transition-transform duration-[20000ms] ease-linear ${mounted ? 'scale-105' : 'scale-100'}`}>
                    <StoreImage src={imageUrl} mobileSrc={mobileImageUrl || undefined} width={1600} alt={altText} className="w-full h-full object-cover" />
                </div>
            </div>
            
            {/* Darker Overlay */}
            <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>

            {/* Content Wrapper */}
            <div className="absolute inset-0 px-[6%] py-[6%] pointer-events-none z-10">
                <div className="relative w-full h-full">
                    <div 
                        className={`
                            pointer-events-auto
                            transition-all duration-1000 delay-300 ease-out 
                            absolute
                            flex flex-col 
                            ${heroTextAlignment === 'center' ? 'items-center' : heroTextAlignment === 'right' ? 'items-end' : 'items-start'}
                            ${mounted ? 'opacity-100' : 'opacity-0 translate-y-6'}
                        `}
                        style={{
                            left: `${cssLeftPercent}%`,
                            top: `${cssTopPercent}%`,
                            transform: `translate(-${isDesktop ? cssLeftPercent : 50}%, -${cssTopPercent}%)`,
                            textAlign: heroTextAlignment,
                            width: '100%',
                            maxWidth: isDesktop ? '90%' : '100%'
                        }}
                    >
                        {customBadge ? customBadge : (student && (
                            <div className="mb-6 tracking-[0.3em] text-[9px] font-black uppercase text-white/80 border-b border-white/20 pb-2 inline-block">
                                Stöd {student.name} {className ? `& ${className}` : ''}
                            </div>
                        ))}

                        <h1 
                            className={`${props.titleSizeClass} mb-6 leading-[0.95] ${dynamicShadow} max-w-4xl ${textureClass}`}
                            style={{ 
                                color: textureClass ? 'transparent' : titleColor, 
                                ...fontStyle,
                                filter: textureClass ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))' : undefined,
                                ...titleOutline
                            }}
                        >
                            {title}
                        </h1>
                        
                        {text && (
                            <p 
                                className={`text-white/80 max-w-sm mb-10 font-sans text-sm md:text-base leading-relaxed tracking-wide font-medium drop-shadow-md ${props.textShadowClass}`}
                                style={{
                                    ...textOutline
                                }}
                            >
                                {text}
                            </p>
                        )}

                        {customButtons}
                    </div>
                </div>
            </div>
        </div>
    );
};
